'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/ui/page-header';
import { useAccountManager } from '@/hooks/use-account-manager';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { useAccountPlanForSettings, useAccountBuyRateFloor } from '@/lib/api/account-manager';
import { UTCTimeDisplay } from './components/UTCTimeDisplay';
import { ManualBillingTab } from './components/ManualBillingTab';
import { PlanSettingsTab } from './components/PlanSettingsTab';
import { BillingProviderCard } from './components/BillingProviderCard';
import { AccessControlTab } from './components/AccessControlTab';
import { AccountStatusTab } from './components/AccountStatusTab';
import { AiCreditsTab } from './components/AiCreditsTab';
import { BillingCreditsTab } from './components/BillingCreditsTab';
import { ProductSyncTab } from './components/ProductSyncTab';
import { ReferralPayoutsTab } from './components/ReferralPayoutsTab';
import { Loader2 } from 'lucide-react';

export default function AccountManagerPage() {
  const { t } = useTranslation('common');
  const { isAccountManager, isLoading: isLoadingRole } = useAccountManager();
  const { isPartnerAdmin, isSuperAdmin, whitelabelScope } = useSessionClaims();
  // Full Tagada staff (no partner scope) sees every tab. Partner admins and
  // partner-scoped account managers only get the partner tabs; the buy-rate
  // floor keeps their pricing edits at or above Tagada's signed buy rate.
  const isStaffManager = (isAccountManager || isSuperAdmin) && !whitelabelScope;
  const canAccess = isAccountManager || isSuperAdmin || isPartnerAdmin;
  const [activeTab, setActiveTab] = useState<
    | 'account-status'
    | 'manual-billing'
    | 'plan-settings'
    | 'access-control'
    | 'ai-credits'
    | 'billing-credits'
    | 'product-sync'
    | 'referral-payouts'
  >('account-status');
  const [currentUTCTime, setCurrentUTCTime] = useState<Date | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  // Fetch account plan
  const {
    data: accountPlan,
    isLoading: isLoadingAccountPlan,
    refetch: refetchAccountPlan,
  } = useAccountPlanForSettings('scale');

  // Buy-rate floor for this merchant (null for direct/legacy merchants).
  const { data: buyRateFloor } = useAccountBuyRateFloor(accountPlan?.accountId);

  // Update UTC time every second
  useEffect(() => {
    setIsMounted(true);
    setCurrentUTCTime(new Date());

    const timer = setInterval(() => {
      setCurrentUTCTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Show loading while checking role
  if (isLoadingRole) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>{t('accountManager.page.checkingPermissions')}</span>
        </div>
      </div>
    );
  }

  // Show access denied if neither account manager nor partner admin
  if (!canAccess) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-4">
        <h2 className="text-xl font-semibold">{t('accountManager.page.accessDeniedTitle')}</h2>
        <p className="text-muted-foreground">{t('accountManager.page.accessDeniedDescription')}</p>
      </div>
    );
  }

  // Partner admins are scoped to pricing + account status for now.
  // TODO(staff-am-only): after rollout, restrict Account Status tab to isStaffManager only.
  const PARTNER_VISIBLE_TABS = ['account-status', 'plan-settings', 'billing-credits'] as const;
  const effectiveTab = isStaffManager
    ? activeTab
    : PARTNER_VISIBLE_TABS.includes(activeTab as (typeof PARTNER_VISIBLE_TABS)[number])
      ? activeTab
      : 'account-status';

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      <PageHeader title={t('accountManager.page.title')} description={t('accountManager.page.description')} />

      <Tabs value={effectiveTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full">
        <div className="flex items-center justify-between">
          <TabsList>
            {/* TODO(staff-am-only): wrap with isStaffManager after rollout */}
            <TabsTrigger value="account-status" className="p-4">
              {t('accountManager.page.tabs.accountStatus')}
            </TabsTrigger>
            {isStaffManager && (
              <TabsTrigger value="manual-billing" className="p-4">
                {t('accountManager.page.tabs.manualBilling')}
              </TabsTrigger>
            )}
            <TabsTrigger value="plan-settings" className="p-4">
              {t('accountManager.page.tabs.planSettings')}
            </TabsTrigger>
            {isStaffManager && (
              <TabsTrigger value="access-control" className="p-4">
                {t('accountManager.page.tabs.accessControl')}
              </TabsTrigger>
            )}
            {isStaffManager && (
              <TabsTrigger value="ai-credits" className="p-4">
                {t('accountManager.page.tabs.aiCredits')}
              </TabsTrigger>
            )}
            <TabsTrigger value="billing-credits" className="p-4">
              {t('accountManager.page.tabs.billingCredits')}
            </TabsTrigger>
            {isStaffManager && (
              <TabsTrigger value="product-sync" className="p-4">
                {t('accountManager.page.tabs.productSync')}
              </TabsTrigger>
            )}
            {isStaffManager && (
              <TabsTrigger value="referral-payouts" className="p-4">
                {t('accountManager.page.tabs.referralPayouts')}
              </TabsTrigger>
            )}
          </TabsList>
          <UTCTimeDisplay currentTime={currentUTCTime} isMounted={isMounted} />
        </div>

        {/* Tab 0: Account Status — visible to all AM page users for now */}
        {/* TODO(staff-am-only): {isStaffManager && ( ... )} after rollout */}
        <TabsContent value="account-status" className="mt-6 space-y-6">
          <AccountStatusTab accountId={accountPlan?.accountId} />
        </TabsContent>

        {/* Tab 1: Manual Billing Trigger */}
        {isStaffManager && (
          <TabsContent value="manual-billing" className="mt-6 space-y-6">
            <ManualBillingTab
              accountId={accountPlan?.accountId}
              accountPlanLoading={isLoadingAccountPlan}
              billingDisabled={accountPlan?.billingDisabled === true}
            />
          </TabsContent>
        )}

        {/* Tab 2: Plan Settings */}
        <TabsContent value="plan-settings" className="mt-6 space-y-6">
          {isStaffManager && <BillingProviderCard />}
          <PlanSettingsTab
            accountPlan={accountPlan}
            refetch={refetchAccountPlan}
            floor={(buyRateFloor?.floor ?? null) as Record<string, number> | null}
          />
        </TabsContent>

        {/* Tab 3: Access Control */}
        {isStaffManager && (
          <TabsContent value="access-control" className="mt-6 space-y-6">
            <AccessControlTab accountId={accountPlan?.accountId} />
          </TabsContent>
        )}

        {/* Tab 5: AI Credits */}
        {isStaffManager && (
          <TabsContent value="ai-credits" className="mt-6 space-y-6">
            <AiCreditsTab accountId={accountPlan?.accountId} />
          </TabsContent>
        )}

        {/* Tab 5b: Billing Credits — staff AMs + partner admins (backend enforces scope) */}
        <TabsContent value="billing-credits" className="mt-6 space-y-6">
          <BillingCreditsTab accountId={accountPlan?.accountId} />
        </TabsContent>

        {/* Tab 6: Product Sync */}
        {isStaffManager && (
          <TabsContent value="product-sync" className="mt-6 space-y-6">
            <ProductSyncTab accountId={accountPlan?.accountId} />
          </TabsContent>
        )}

        {/* Tab 7: Referral payouts — global, not per account */}
        {isStaffManager && (
          <TabsContent value="referral-payouts" className="mt-6 space-y-6">
            <ReferralPayoutsTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
